import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Banknote, IndianRupee, Landmark, RotateCcw, Smartphone, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { SegmentedControl } from '@/components/ui/segmented';
import { StatCard } from '@/components/ui/stat-card';
import { Skeleton } from '@/components/ui/skeleton';
import { use_transactions } from '@/features/vendor-stats/hooks';
import type { LedgerEntry, TxRange } from '@/features/vendor-stats/types';
import { format_inr } from '@/lib/utils';
import { date_label } from '@/lib/datetime';

const RANGE_OPTIONS: { value: TxRange; label: string }[] = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '12m', label: '12 months' },
  { value: 'all', label: 'All time' },
];

function fmt_date(iso: string): string {
  return date_label(iso);
}

const TYPE_LABEL: Record<LedgerEntry['type'], string> = {
  BOOKING: 'Booking',
  ORDER: 'Order',
  SHIPMENT: 'Shipment',
  REFUND: 'Refund',
};

function LedgerRow({ tx }: { tx: LedgerEntry }) {
  const credit = tx.direction === 'CREDIT';
  const Icon = credit ? ArrowUpRight : ArrowDownLeft;
  return (
    <div className="flex items-center gap-3 py-3" data-testid="ledger-row">
      <span
        className={
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-button ' +
          (credit ? 'bg-success/12 text-success' : 'bg-destructive/12 text-destructive')
        }
      >
        {tx.type === 'REFUND' ? <RotateCcw className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight">{tx.label}</p>
        <p className="text-xs text-muted-foreground">
          {TYPE_LABEL[tx.type]} · {fmt_date(tx.date)}
        </p>
      </div>
      <span className={'shrink-0 font-heading text-sm font-bold tabular-nums ' + (credit ? 'text-success' : 'text-destructive')}>
        {credit ? '+' : '−'}
        {format_inr(tx.amount_inr)}
      </span>
    </div>
  );
}

export function VendorPaymentsPage() {
  const [range, setRange] = useState<TxRange>('90d');
  const tx_q = use_transactions(range);
  const data = tx_q.data;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader title="Payments & Payouts" description="Track your earnings, refunds and where you get paid." />

      <div className="max-w-md">
        <SegmentedControl value={range} onChange={setRange} options={RANGE_OPTIONS} testId="tx-range" aria-label="Ledger range" />
      </div>

      {tx_q.isLoading || !data ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[104px] rounded-card" />
            ))}
          </div>
          <Skeleton className="h-64 rounded-card" />
        </>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="payments-summary">
            <StatCard label="Net earnings" value={format_inr(data.summary.net_inr)} icon={Wallet} hint="Gross minus refunds" />
            <StatCard label="Gross earned" value={format_inr(data.summary.gross_earned_inr)} icon={IndianRupee} hint={`${data.summary.completed_jobs} completed`} />
            <StatCard label="Refunded" value={format_inr(data.summary.refunded_inr)} icon={RotateCcw} />
            <StatCard label="This month (net)" value={format_inr(data.summary.this_month_net_inr)} icon={Banknote} />
          </div>

          {/* Payout destination */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Payout destination</CardTitle>
            </CardHeader>
            <CardContent>
              {data.payout_account ? (
                <div className="flex items-center gap-3" data-testid="payout-account">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-button bg-secondary text-primary shadow-clay-sm">
                    {data.payout_account.method === 'UPI' ? <Smartphone className="h-5 w-5" /> : <Landmark className="h-5 w-5" />}
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{data.payout_account.method === 'UPI' ? 'UPI' : 'Bank account'}</p>
                    <p className="text-sm text-muted-foreground tabular-nums">{data.payout_account.display}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="payout-empty">
                  No payout account on file yet. You can add one during onboarding so earnings can be settled to you.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Ledger */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {data.transactions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground" data-testid="ledger-empty">
                  No transactions in this period yet.
                </p>
              ) : (
                <div className="divide-y divide-border" data-testid="ledger">
                  {data.transactions.map((tx) => (
                    <LedgerRow key={tx.id} tx={tx} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
